<template>
  <div class="forecast-card bg-white-1 shadow3 q-pa-lg q-pr-xl q-pb-xl">
    <div class="text-heading-2 text-secondary-1">Forecast</div>
    <div class="text-caption-3 text-secondary-5">
      Select subscriptions and enter the increase you wish to forecast
    </div>

    <Grid gtc="1fr 1fr 1fr" class="q-mt-lg forecast-actions text-secondary-1">
      <div class="selection">
        <div :class="[!numberOfSelectedRows ? 'text-overline-1' : 'text-caption-1']">
          1. SELECTION
        </div>
        <LineArrow />
        <div class="subs-selected">
          <div class="text-body-1 text-secondary-5">Subscriptions Selected:</div>
          <Grid gaf="column" justify-content="start" align-items="center" gap="3px" class="text-secondary-1">
            <span class="text-heading-3">{{ numberOfSelectedRows }}</span>
            /
            <span class="text-caption-3">{{ overviewData && overviewData.total_subscription_count }}</span>
          </Grid>
        </div>
      </div>

      <div class="percentage" :data-disable="!numberOfSelectedRows">
        <div :class="[numberOfSelectedRows && !percent ? 'text-overline-1' : 'text-caption-1']">2. PERCENTAGE</div>
        <LineArrow />

        <q-input
          ref="percentInput"
          :value="percent"
          class="percent-input q-mt-xs q-ml-xs"
          :disable="!numberOfSelectedRows"
          dense
          outlined
        >
          <template #append>
            <q-icon name="fas fa-percent" size="12px" color="grey2"></q-icon>
          </template>
        </q-input>
      </div>

      <div class="additional-options" :data-disable="!percent">
        <div :class="[percent ? 'text-overline-1' : 'text-caption-1']">3. ADDITIONAL OPTIONS</div>
        <DotArrow />
        <Grid justify-content="start">
          <RCheckbox v-model="overwrite" class="full-width" :disable="!percent" label="Overwrite existing forecasts" />
        </Grid>
      </div>
    </Grid>

    <Grid justify-content="end" class="q-mt-lg">
      <RBtn :loading="loadingBtn" :disable="!percent" @click="applyOnSelection">Apply on selection</RBtn>
    </Grid>
  </div>
</template>

<script>
// Libraries
import { mapState } from 'vuex'
import Cleave from 'cleave.js'
// Components
import LineArrow from '@/reusable/svg-icons/LineArrow'
import DotArrow from '@/reusable/svg-icons/DotArrow'

export default {
  components: {
    LineArrow,
    DotArrow
  },

  data: () => ({
    percent: null,
    rawPercent: null,
    overwrite: false,
    loadingBtn: false
  }),

  computed: {
    ...mapState('price-forecast', ['rowsSelected', 'selectAll', 'overviewData', 'totalRows']),

    numberOfSelectedRows() {
      if (this.selectAll) {
        return this.totalRows - this.rowsSelected.length
      }
      return this.rowsSelected.length
    }
  },

  watch: {
    rowsSelected(rows) {
      if (!rows.length) {
        // reset data when no rows are selected
        this.resetData()
      }
    }
  },

  mounted() {
    const delimiter = this.settings.thousand_separator

    this.cleave = new Cleave(this.$el.querySelector('.percent-input input'), {
      numeral: true,
      numeralDecimalMark: this.settings.decimal_separator,
      delimiter: delimiter === 'space' ? ' ' : delimiter,
      numeralDecimalScale: 2,
      onValueChanged: ({ target: { rawValue, value } }) => {
        this.rawPercent = Number(rawValue)
        this.percent = value
      }
    })

    this.cleave.setRawValue(this.percent)
    this.$nextTick(() => {
      this.$refs.percentInput.focus()
    })
  },

  methods: {
    resetData() {
      this.percent = null
      this.rawPercent = null
      this.overwrite = false
    },

    setLoadingBtnState(state) {
      this.loadingBtn = state
    },

    applyOnSelection() {
      const includedOrExcludedIds = this.selectAll ? 'excluded_ids' : 'included_ids'

      this.$emit('apply-on-selection', {
        forecasted_change_percentage: this.rawPercent,
        [includedOrExcludedIds]: this.rowsSelected.map(row => row.id),
        overwrite_existing_forecasts: this.overwrite
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';
.forecast-card {
  grid-area: forecast-card;
  height: 290px;

  .forecast-actions {
    height: 90px;
    padding-right: 65px;

    .percent-input {
      @include quasar-field-control-style {
        --input-height: 25px;
        padding-left: var(--r-space-sm) !important;
        width: 70px;
        border: 1px solid var(--r-white-5);

        input {
          @extend .text-body-1;
          color: var(--r-secondary-1);
          text-align: center;
        }

        .q-field__append {
          width: 16px;
        }
      }
    }

    > *[data-disable] * {
      color: var(--r-grey-2);
    }
  }
}
</style>
